import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Search, Users, Phone, Shield, ChevronRight } from "lucide-react";

const API = "/api";

export default function PacientesPage() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${search}` : "";
      const res = await axios.get(`${API}/pacientes${params}`, { headers: getAuthHeader() });
      setPacientes(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pacientes</h1>
          <p className="text-slate-500 text-sm mt-0.5">{pacientes.length} pacientes registrados</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar por nombre, apellido, DNI..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10 !py-2.5"
          data-testid="search-pacientes"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : pacientes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users size={40} className="text-slate-200 mb-3" />
          <p className="text-slate-400 font-medium">No se encontraron pacientes</p>
          <p className="text-slate-300 text-sm mt-1">Los pacientes aparecen automáticamente desde los turnos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pacientes.map((p, idx) => (
            <div
              key={p.dni || idx}
              onClick={() => p.dni && navigate(`/paciente/${p.dni}`)}
              className={`bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md hover:border-blue-100 transition-all ${p.dni ? "cursor-pointer" : ""}`}
              data-testid="paciente-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-700 font-bold text-sm">
                      {(p.nombre?.[0] || "?").toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">
                      {p.nombre} {p.apellido}
                    </p>
                    {p.dni && (
                      <p className="text-xs text-slate-400 mt-0.5">DNI: {p.dni}</p>
                    )}
                  </div>
                </div>
                {p.dni && <ChevronRight size={16} className="text-slate-300 flex-shrink-0 mt-1" />}
              </div>

              <div className="mt-3 space-y-1.5">
                {p.telefono && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Phone size={12} />
                    {p.telefono}
                  </div>
                )}
                {p.cobertura && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Shield size={12} />
                    {p.cobertura}
                  </div>
                )}
                {p.email && (
                  <p className="text-xs text-slate-400 truncate">{p.email}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
